// AI管理コンソール — RAG / 利用状況 / テスト
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DatabaseZap, Play, Sparkles, Globe, Search, Plus, Trash2, BookOpenText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API } from './AiConsole';
import { Card } from './consoleTabs';
import { jstDateTimeShort } from '@/utils/jstDate';

// ── RAG設定 ──────────────────────────────────────────────────────────────────
export function RagSection({ settings, models, save, saving, isCustom }) {
  const [rag, setRag] = useState(settings.rag || {});
  const [status, setStatus] = useState(null);
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null);
  const embedModels = models.filter((m) => m.type === 'embedding');
  const set = (k, v) => setRag((r) => ({ ...r, [k]: v }));

  const loadStatus = () => axios.get(`${API}/rag/status`, { withCredentials: true })
    .then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);
  useEffect(() => {
    if (status?.status?.state !== 'running') return undefined;
    const t = setInterval(loadStatus, 3000);
    return () => clearInterval(t);
  }, [status?.status?.state]);

  const reindex = async () => {
    try {
      await axios.post(`${API}/rag/reindex`, {}, { withCredentials: true });
      toast.success('再索引を開始しました（バックグラウンド実行）');
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || '再索引の開始に失敗しました'); }
  };
  const ask = async () => {
    setAsking(true); setResult(null);
    try {
      const r = await axios.post(`${API}/rag/ask`, { question: q }, { withCredentials: true });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'RAG検索に失敗しました'); } finally { setAsking(false); }
  };

  const st = status?.status || {};
  const pct = st.total ? Math.round((st.done / st.total) * 100) : 0;
  return (
    <div className="space-y-4">
      <Card title="RAG（ナレッジ検索の索引）" testid="ai-console-rag"
        hint="ナレッジ記事をAIが検索できる形（埋め込み）に索引化します。有効にすると、ナレッジアシスタントの回答に関連記事の引用が自動で添えられ、記事の作成・編集・削除時は自動で索引が更新されます。">
        <div className="flex items-center justify-between rounded-lg border border-zinc-200/60 px-4 py-3">
          <div>
            <p className="text-[13px] font-semibold text-zinc-800">RAGを有効化</p>
            <p className="text-[11px] text-zinc-400">ローカルLLM + 埋め込みモデルが必要です</p>
          </div>
          <Switch checked={!!rag.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="ai-rag-enabled" />
        </div>
        <div data-testid="ai-rag-embed-model-readonly">
          <label className="text-[10px] font-semibold text-zinc-500">埋め込みモデル（表示のみ）</label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-zinc-200/60 bg-zinc-50 px-3 py-2">
            <span className="flex-1 text-[12px] font-mono text-zinc-800">
              {rag.embedding_model || '未選択（機能ルーティングの「埋め込み」割り当てを使用）'}
            </span>
            <a href="#routing" className="text-[11px] font-semibold text-teal-700 underline"
              data-testid="ai-rag-embed-model-goto-routing">
              機能ルーティングで変更
            </a>
          </div>
          <p className="mt-1 text-[10px] text-zinc-500">
            埋め込みモデルは「AI機能ごとのモデル設定」の <span className="font-mono">embeddings</span> ルートが唯一の変更元です。
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-zinc-200/60 px-3 py-2.5">
            <div>
              <p className="text-[12px] font-semibold text-zinc-700">クエリ書き換え</p>
              <p className="text-[10px] text-zinc-400">検索前にLLMが質問を検索キーワードへ変換（精度↑・応答は少し遅くなる）</p>
            </div>
            <Switch checked={!!rag.query_rewrite} onCheckedChange={(v) => set('query_rewrite', v)} data-testid="ai-rag-query-rewrite" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-zinc-200/60 px-3 py-2.5">
            <div>
              <p className="text-[12px] font-semibold text-zinc-700">前後チャンク拡張</p>
              <p className="text-[10px] text-zinc-400">ヒット断片の前後もあわせて文脈に使用（文の途切れを防ぐ）</p>
            </div>
            <Switch checked={rag.neighbor_expand !== false} onCheckedChange={(v) => set('neighbor_expand', v)} data-testid="ai-rag-neighbor-expand" />
          </div>
        </div>
        <Accordion type="single" collapsible>
          <AccordionItem value="adv" className="rounded-lg border border-zinc-200/60 px-3">
            <AccordionTrigger className="py-2.5 text-xs font-semibold text-zinc-600 hover:no-underline" data-testid="ai-rag-advanced-toggle">
              詳細設定（チャンク・検索精度）
            </AccordionTrigger>
            <AccordionContent className="pb-3">
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {[['chunk_size', 'チャンクサイズ', 1500], ['chunk_overlap', 'オーバーラップ', 200],
                  ['top_k', 'Top K 取得数', 8], ['similarity', '類似度しきい値', 0.75]].map(([k, label, ph]) => (
                  <div key={k}>
                    <label className="text-[10px] font-semibold text-zinc-500">{label}</label>
                    <Input type="number" step="any" value={rag[k] ?? ''} placeholder={String(ph)}
                      onChange={(e) => set(k, e.target.value === '' ? null : Number(e.target.value))}
                      className="h-8 bg-white text-xs" data-testid={`ai-rag-${k}`} />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => save({ rag })} disabled={saving}
            className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-rag-save">設定を保存</Button>
          <Button size="sm" variant="outline" onClick={reindex} disabled={!isCustom || st.state === 'running'}
            className="h-9 text-xs" data-testid="ai-rag-reindex">
            <DatabaseZap size={13} strokeWidth={1.5} className="mr-1.5" /> 全件を再索引
          </Button>
        </div>
        {st.state === 'running' ? (
          <div className="space-y-1.5" data-testid="ai-rag-status">
            <Progress value={pct} className="h-2" />
            <p className="text-[11px] text-zinc-500">索引中… {st.done}/{st.total} 記事（{st.chunks} チャンク・{pct}%）</p>
          </div>
        ) : (
          <p className="text-[11px] text-zinc-500" data-testid="ai-rag-status">
            {st.state === 'done' ? `✓ 索引済み: ${status?.indexed_chunks ?? 0} チャンク（${st.total} 記事）— 記事の変更は自動反映されます`
              : st.state === 'error' ? `✗ エラー: ${st.error}`
                : `索引済みチャンク: ${status?.indexed_chunks ?? 0}`}
          </p>
        )}
      </Card>

      <Card title="RAG検索テスト" hint="索引済みナレッジから引用付きで回答できるか試せます。">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="質問を入力…"
            className="h-9 text-sm" data-testid="ai-rag-question" />
          <Button size="sm" onClick={ask} disabled={asking || !q.trim() || !isCustom}
            className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-rag-ask">
            <Sparkles size={13} strokeWidth={1.5} className="mr-1" /> {asking ? '検索中…' : '検索'}
          </Button>
        </div>
        {result && (
          <div className="space-y-2 rounded-lg border border-zinc-200/60 bg-zinc-50 p-4" data-testid="ai-rag-result">
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-800">{result.answer}</p>
            {(result.citations || []).length > 0 && (
              <div className="space-y-0.5 border-t border-zinc-200 pt-2">
                {result.citations.map((c) => (
                  <a key={c.n} href={c.source_type === 'doc' ? `/documents?open=${c.article_id}` : `/knowledge/p/${c.article_id}`}
                    target="_blank" rel="noreferrer"
                    className="group flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-indigo-700"
                    data-testid={`ai-rag-citation-${c.n}`}>
                    <span className={`shrink-0 rounded border px-1 py-px text-[9px] font-bold ${c.source_type === 'doc'
                      ? 'border-violet-100 bg-violet-50 text-violet-600'
                      : 'border-indigo-100 bg-indigo-50 text-indigo-600'}`}>
                      {c.source_type === 'doc' ? '書類' : 'ナレッジ'}
                    </span>
                    <span className="truncate group-hover:underline">[{c.n}] {c.title}{c.doc_number ? ` ${c.doc_number}` : ''}（関連度 {c.score}）</span>
                  </a>))}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── プロンプトと用語集 ──────────────────────────────────────────────────────────
export function PromptsSection({ features, settings, save, saving }) {
  const [prompts, setPrompts] = useState(() => ({ ...(settings.custom_prompts || {}) }));
  const [glossary, setGlossary] = useState(() => (settings.glossary || []).map((t) => ({ ...t })));
  const [shots, setShots] = useState(() => JSON.parse(JSON.stringify(settings.few_shots || {})));
  const [target, setTarget] = useState('global');
  const textFeatures = (features || []).filter((f) => f.active && f.kind === 'text');

  const doSave = () => save({ custom_prompts: prompts, glossary, few_shots: shots });
  const curShots = shots[target] || [];
  const setCurShots = (list) => setShots((s) => ({ ...s, [target]: list }));

  return (
    <div className="space-y-4">
      <Card title="カスタム システムプロンプト" testid="ai-console-prompts"
        hint="ここに書いた指示は、AIへの全リクエストのシステムプロンプトに自動追加されます。会社固有のルール（回答スタイル・禁止事項・前提知識など）を書くと、どの機能でもAIがそれに従います。">
        <div>
          <label className="text-[10px] font-semibold text-zinc-500">適用対象</label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="h-9 bg-white text-xs" data-testid="ai-prompts-target"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="global">全機能共通（グローバル）</SelectItem>
              {textFeatures.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.label_ja}{(prompts[f.id] || '').trim() ? ' ●' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500">
            {target === 'global' ? '全機能共通のプロンプト' : `「${textFeatures.find((f) => f.id === target)?.label_ja || target}」専用のプロンプト（共通の後に追加）`}
          </label>
          <textarea value={prompts[target] || ''} maxLength={2000}
            onChange={(e) => setPrompts((p) => ({ ...p, [target]: e.target.value }))}
            rows={7} placeholder={'例:\n・当社は神奈川県の自動はんだ付け装置メーカー「株式会社センスビー」です\n・回答は必ず日本語で、結論を先に述べること\n・不確かな情報は「未確認」と明記すること'}
            className="mt-1 w-full resize-y rounded-lg border border-zinc-200 bg-white p-3 font-mono text-[12px] leading-relaxed outline-none focus:border-indigo-300"
            data-testid="ai-prompts-textarea" />
          <p className="text-right text-[10px] text-zinc-400">{(prompts[target] || '').length} / 2000</p>
        </div>
      </Card>

      <Card title="社内用語集" testid="ai-console-glossary"
        hint="製品名・略語・社内用語の定義です。AIが用語を誤解しないよう、全リクエストに自動で添付されます（最大100件）。">
        <div className="space-y-2">
          {glossary.map((t, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`ai-glossary-row-${i}`}>
              <Input value={t.term} maxLength={60} placeholder="用語（例: SBY-100）"
                onChange={(e) => setGlossary((g) => g.map((x, j) => j === i ? { ...x, term: e.target.value } : x))}
                className="h-9 w-44 shrink-0 bg-white text-xs font-mono" />
              <Input value={t.definition} maxLength={200} placeholder="意味・定義（例: 卓上型自動はんだ付け装置の主力モデル）"
                onChange={(e) => setGlossary((g) => g.map((x, j) => j === i ? { ...x, definition: e.target.value } : x))}
                className="h-9 flex-1 bg-white text-xs" />
              <button type="button" onClick={() => setGlossary((g) => g.filter((_, j) => j !== i))}
                className="shrink-0 rounded-md p-2 text-zinc-300 transition-colors hover:bg-rose-50 hover:text-rose-500"
                data-testid={`ai-glossary-remove-${i}`}>
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </div>
          ))}
          {glossary.length === 0 && (
            <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-4 text-center text-[11px] text-zinc-400">まだ用語がありません — 「用語を追加」から登録してください</p>
          )}
          <Button size="sm" variant="outline" disabled={glossary.length >= 100}
            onClick={() => setGlossary((g) => [...g, { term: '', definition: '' }])}
            className="h-8 text-[11px]" data-testid="ai-glossary-add">
            <Plus size={12} className="mr-1" /> 用語を追加（{glossary.length}/100）
          </Button>
        </div>
      </Card>

      <Card title="回答例（Few-shot）" testid="ai-console-fewshots"
        hint="機能ごとに理想的な質問と回答の例を最大3組登録できます。AIはこの例のスタイル・形式を真似て回答します（全機能共通には設定できません — 上の適用対象で機能を選択）。">
        {target === 'global' ? (
          <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-4 text-center text-[11px] text-zinc-400">上の「適用対象」で機能を選択すると回答例を編集できます</p>
        ) : (
          <div className="space-y-3">
            {curShots.map((x, i) => (
              <div key={i} className="rounded-lg border border-zinc-200/60 p-3 space-y-2" data-testid={`ai-fewshot-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold text-zinc-400">例 {i + 1}</p>
                  <button type="button" onClick={() => setCurShots(curShots.filter((_, j) => j !== i))}
                    className="rounded p-1 text-zinc-300 hover:bg-rose-50 hover:text-rose-500" data-testid={`ai-fewshot-remove-${i}`}>
                    <Trash2 size={13} strokeWidth={1.5} />
                  </button>
                </div>
                <Input value={x.q} maxLength={1000} placeholder="質問の例"
                  onChange={(e) => setCurShots(curShots.map((y, j) => j === i ? { ...y, q: e.target.value } : y))}
                  className="h-9 bg-white text-xs" />
                <textarea value={x.a} maxLength={2000} rows={3} placeholder="理想的な回答の例"
                  onChange={(e) => setCurShots(curShots.map((y, j) => j === i ? { ...y, a: e.target.value } : y))}
                  className="w-full resize-y rounded-lg border border-zinc-200 bg-white p-2.5 text-[12px] leading-relaxed outline-none focus:border-indigo-300" />
              </div>
            ))}
            <Button size="sm" variant="outline" disabled={curShots.length >= 3}
              onClick={() => setCurShots([...curShots, { q: '', a: '' }])}
              className="h-8 text-[11px]" data-testid="ai-fewshot-add">
              <Plus size={12} className="mr-1" /> 例を追加（{curShots.length}/3）
            </Button>
          </div>
        )}
      </Card>

      <Button size="sm" onClick={doSave} disabled={saving}
        className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-prompts-save">
        {saving ? '保存中…' : 'プロンプトと用語集を保存'}
      </Button>
    </div>
  );
}

// ── Web検索 ──────────────────────────────────────────────────────────────────
const ENGINES = [
  ['duckduckgo', 'DuckDuckGo（無料・キー不要）'],
  ['brave', 'Brave Search（APIキーが必要）'],
  ['searxng', 'SearXNG（自社ホスト）'],
];

export function WebSearchSection({ settings, save, saving }) {
  const [ws, setWs] = useState(() => ({ ...(settings.web_search || {}), brave_api_key: null }));
  const [q, setQ] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setWs((w) => ({ ...w, [k]: v }));

  const doSave = () => save({ web_search: {
    enabled: !!ws.enabled, engine: ws.engine || 'duckduckgo',
    max_results: ws.max_results || 5, searxng_url: ws.searxng_url || '',
    brave_api_key: ws.brave_api_key, // null = 変更なし
  } });

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await axios.post(`${API}/web-search/test`, { query: q }, { withCredentials: true });
      setResult(r.data);
      if (!r.data.ok) toast.error(r.data.error || '検索に失敗しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '検索テストに失敗しました'); } finally { setTesting(false); }
  };

  return (
    <div className="space-y-4">
      <Card title="Web検索（インターネットアクセス）" testid="ai-console-websearch"
        hint="有効にすると、チャットのAIアシスタントが必要に応じてインターネット検索を行い、検索結果を根拠に回答します（参照元に Web バッジ付きで表示）。ローカルLLM・Emergentのどちらのモードでも利用できます。">
        <div className="flex items-center justify-between rounded-lg border border-zinc-200/60 px-4 py-3">
          <div>
            <p className="text-[13px] font-semibold text-zinc-800">Web検索を有効化</p>
            <p className="text-[11px] text-zinc-400">AIアシスタントのツールとして「Web検索」が追加されます</p>
          </div>
          <Switch checked={!!ws.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="ai-websearch-enabled" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[10px] font-semibold text-zinc-500">検索エンジン</label>
            <Select value={ws.engine || 'duckduckgo'} onValueChange={(v) => set('engine', v)}>
              <SelectTrigger className="h-9 bg-white text-xs" data-testid="ai-websearch-engine"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {ENGINES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-zinc-500">最大取得件数（1〜10）</label>
            <Input type="number" min={1} max={10} value={ws.max_results ?? 5}
              onChange={(e) => set('max_results', Number(e.target.value) || 5)}
              className="h-9 bg-white text-xs" data-testid="ai-websearch-max-results" />
          </div>
        </div>
        {ws.engine === 'brave' && (
          <div>
            <label className="text-[10px] font-semibold text-zinc-500">Brave Search APIキー</label>
            <Input type="password" value={ws.brave_api_key ?? ''}
              onChange={(e) => set('brave_api_key', e.target.value)}
              placeholder={settings.web_search?.has_brave_key ? '●●●●●●（設定済み — 変更しない場合は空のまま）' : 'BSA...'}
              className="h-9 bg-white text-xs" data-testid="ai-websearch-brave-key" />
            <p className="mt-1 text-[10px] text-zinc-400">brave.com/search/api で無料プラン（月2,000クエリ）のキーを取得できます</p>
          </div>
        )}
        {ws.engine === 'searxng' && (
          <div>
            <label className="text-[10px] font-semibold text-zinc-500">SearXNGのURL</label>
            <Input value={ws.searxng_url || ''} onChange={(e) => set('searxng_url', e.target.value)}
              placeholder="https://searx.example.com" className="h-9 bg-white text-xs font-mono" data-testid="ai-websearch-searxng-url" />
            <p className="mt-1 text-[10px] text-zinc-400">このサーバーから到達可能なURLが必要です（NAS内の場合はトンネル経由）。settings.yml で format=json を許可してください</p>
          </div>
        )}
        <Button size="sm" onClick={doSave} disabled={saving}
          className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-websearch-save">設定を保存</Button>
      </Card>

      <Card title="検索テスト" hint="保存済みの設定で実際に検索できるか確認できます。URLを入力するとページ本文の読み取りをテストできます。">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="検索キーワード、または読み取りたいページのURLを入力…"
            onKeyDown={(e) => e.key === 'Enter' && q.trim() && !testing && test()}
            className="h-9 text-sm" data-testid="ai-websearch-test-query" />
          <Button size="sm" onClick={test} disabled={testing || !q.trim()}
            className="h-9 bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-websearch-test-btn">
            <Search size={13} strokeWidth={1.5} className="mr-1" /> {testing ? '検索中…' : '検索'}
          </Button>
        </div>
        {result?.ok && (
          <div className="space-y-2" data-testid="ai-websearch-test-results">
            <p className="text-[10px] font-bold uppercase text-zinc-400">{result.results.length} 件（{result.latency_ms}ms）</p>
            {result.results.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noreferrer"
                className="block rounded-lg border border-zinc-200/60 px-3 py-2 transition-colors hover:bg-indigo-50/40"
                data-testid={`ai-websearch-result-${i}`}>
                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-indigo-700">
                  <Globe size={11} className="shrink-0" /> <span className="truncate">{r.title}</span>
                  {r.fetched && <span className="shrink-0 rounded border border-emerald-100 bg-emerald-50 px-1 py-px text-[8px] font-bold text-emerald-600">本文取得</span>}
                </p>
                <p className="truncate text-[10px] text-zinc-400">{r.url}</p>
                <p className="mt-0.5 line-clamp-2 text-[11px] text-zinc-600">{r.snippet}</p>
              </a>
            ))}
          </div>
        )}
        {result && !result.ok && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700" data-testid="ai-websearch-test-error">
            ✗ {result.error}
          </p>
        )}
      </Card>
    </div>
  );
}

// ── 利用状況 ──────────────────────────────────────────────────────────────────
function StatTable({ rows, keyName, label }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200/60">
      <table className="w-full text-xs">
        <thead className="bg-zinc-50 text-[10px] text-zinc-500">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">{label}</th>
            {['リクエスト', '入力トークン', '出力トークン', '平均応答', 'エラー', '予備使用'].map((h) => (
              <th key={h} className="px-3 py-2 text-right font-semibold">{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[keyName]} className="border-t border-zinc-200 transition-colors duration-200 hover:bg-teal-50/50">
              <td className="px-3 py-2 font-mono font-semibold text-zinc-800">{r[keyName]}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.requests}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.tokens_in.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.tokens_out.toLocaleString()}</td>
              <td className="px-3 py-2 text-right tabular-nums">{(r.avg_latency_ms / 1000).toFixed(1)}s</td>
              <td className={`px-3 py-2 text-right tabular-nums ${r.errors ? 'font-bold text-red-600' : ''}`}>{r.errors}</td>
              <td className={`px-3 py-2 text-right tabular-nums ${r.fallbacks ? 'font-bold text-amber-600' : ''}`}>{r.fallbacks}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="px-3 py-6 text-center text-xs text-zinc-400">まだ利用データがありません</p>}
    </div>
  );
}

export function AnalyticsSection() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/analytics?days=${days}`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);
  return (
    <Card title="利用状況" testid="ai-console-analytics"
      hint="AI呼び出しの回数・トークン・応答速度・エラー・フォールバック使用を集計します（トークンは推定値: 1トークン ≒ 3文字）。">
      <div className="flex items-center gap-1.5">
        {[[1, '今日'], [7, '7日間'], [30, '30日間']].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setDays(v)} data-testid={`ai-analytics-days-${v}`}
            className={`rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-colors duration-200 ${days === v ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'}`}>
            {label}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-zinc-500">合計 {data?.total_requests ?? 0} リクエスト</span>
      </div>
      <p className="text-[11px] font-bold text-zinc-500">モデル別</p>
      <StatTable rows={data?.by_model || []} keyName="model" label="モデル" />
      <p className="text-[11px] font-bold text-zinc-500">機能別</p>
      <StatTable rows={data?.by_feature || []} keyName="feature" label="機能" />
      <p className="text-[11px] font-bold text-zinc-500">Web検索</p>
      <WebSearchStats web={data?.web_search} />
      <p className="text-[11px] font-bold text-zinc-500">回答フィードバック（チャットアシスタント）</p>
      <FeedbackStats fb={data?.feedback} />
    </Card>
  );
}

function FeedbackStats({ fb }) {
  if (!fb || (fb.up === 0 && fb.down === 0)) {
    return <p className="rounded-lg border border-zinc-200/60 px-3 py-5 text-center text-xs text-zinc-400" data-testid="ai-feedback-stats-empty">まだフィードバックがありません — チャットのAIアシスタントの回答に 👍/👎 が付くとここに表示されます</p>;
  }
  return (
    <div className="space-y-2" data-testid="ai-feedback-stats">
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">👍 {fb.up}</span>
        <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-semibold text-rose-700">👎 {fb.down}</span>
        <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-semibold text-zinc-600">
          満足度 {Math.round((fb.up / (fb.up + fb.down)) * 100)}%
        </span>
      </div>
      {(fb.recent_bad || []).length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-zinc-400">最近の低評価 — ナレッジの追加・修正のヒントに</p>
          {fb.recent_bad.map((r, i) => (
            <div key={i} className="rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2" data-testid={`ai-feedback-bad-${i}`}>
              <p className="text-[11px] font-semibold text-zinc-700">Q: {r.question || '（質問不明）'}</p>
              <p className="mt-0.5 line-clamp-2 text-[10.5px] text-zinc-500">A: {r.answer_preview}</p>
              <p className="mt-0.5 text-[9.5px] text-zinc-400">{jstDateTimeShort(r.ts)} · {r.user_name}{r.tool ? ` · ツール: ${r.tool}` : ''}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const WEB_SOURCE_LABELS = {
  assistant: 'チャットアシスタント', chat_panel: '思考パネル',
  knowledge_panel: 'ナレッジパネル', console_test: 'コンソールテスト',
};

function WebSearchStats({ web }) {
  if (!web || web.total === 0) {
    return <p className="rounded-lg border border-zinc-200/60 px-3 py-5 text-center text-xs text-zinc-400" data-testid="ai-websearch-stats-empty">まだWeb検索の利用データがありません</p>;
  }
  return (
    <div className="space-y-2" data-testid="ai-websearch-stats">
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-semibold text-zinc-600">合計 {web.total} 回</span>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${web.errors ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}
          data-testid="ai-websearch-stats-errors">失敗 {web.errors} 回</span>
        {(web.by_source || []).map((s) => (
          <span key={s.source} className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-[11px] font-semibold text-indigo-700">
            {WEB_SOURCE_LABELS[s.source] || s.source} {s.requests}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-200/60">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-[10px] text-zinc-500">
            <tr>
              {['時刻', '呼び出し元', '種別', 'クエリ', '件数', '応答', '状態'].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {(web.recent || []).map((r, i) => (
              <tr key={i} className={`border-t border-zinc-200 ${r.error ? 'bg-rose-50/50' : ''}`}
                data-testid={`ai-websearch-log-${i}`}>
                <td className="whitespace-nowrap px-3 py-2 text-zinc-400">{jstDateTimeShort(r.ts)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-zinc-600">{WEB_SOURCE_LABELS[r.source] || r.source}</td>
                <td className="whitespace-nowrap px-3 py-2 text-zinc-500">{r.mode === 'fetch' ? 'ページ読取' : '検索'}</td>
                <td className="max-w-[260px] truncate px-3 py-2 text-zinc-700" title={r.query}>{r.query}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{r.results}{r.fetched ? ` (本文${r.fetched})` : ''}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{((r.latency_ms || 0) / 1000).toFixed(1)}s</td>
                <td className="whitespace-nowrap px-3 py-2">
                  {r.error
                    ? <span className="font-bold text-rose-600" title={r.error}>✗ 失敗</span>
                    : <span className="text-emerald-600">✓</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {web.errors > 0 && (
        <p className="text-[10px] text-zinc-400">失敗行にカーソルを合わせるとエラー内容が表示されます</p>
      )}
    </div>
  );
}

// ── テスト ────────────────────────────────────────────────────────────────────
export function TestSection({ models, isCustom }) {
  const [model, setModel] = useState('');
  const [prompt, setPrompt] = useState('');
  const [useRag, setUseRag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      const r = await axios.post(`${API}/test`, { model, prompt, use_rag: useRag }, { withCredentials: true });
      setRes(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'テストに失敗しました'); } finally { setBusy(false); }
  };
  return (
    <Card title="モデルテスト" testid="ai-console-test"
      hint="任意のモデルに直接プロンプトを送り、応答時間とトークン/秒を計測します。新しいモデルを入れたらまずここで動作確認を。">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-2.5">
          <Select value={model || '__none__'} onValueChange={(v) => setModel(v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-9 bg-white text-xs" data-testid="ai-test-model"><SelectValue placeholder="モデルを選択" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__none__">モデルを選択…</SelectItem>
              {models.map((m) => <SelectItem key={m.id} value={m.id}>{m.id}（{m.type}）</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6}
            placeholder="テストプロンプトを入力…" className="bg-white text-sm" data-testid="ai-test-prompt" />
          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-zinc-600" data-testid="ai-test-use-rag">
            <input type="checkbox" checked={useRag} onChange={(e) => setUseRag(e.target.checked)}
              className="h-3.5 w-3.5 accent-indigo-600" />
            ナレッジを検索して回答（RAG）— アシスタントと同じ引用付き回答をテスト
          </label>
          <Button onClick={run} disabled={busy || !model || !prompt.trim() || !isCustom}
            className="w-full bg-indigo-600 text-xs hover:bg-indigo-700" data-testid="ai-test-run">
            <Play size={13} strokeWidth={1.5} className="mr-1.5" /> {busy ? '実行中…' : 'テスト実行'}
          </Button>
          {!isCustom && <p className="text-[10.5px] text-amber-600">ローカルLLMプロバイダー有効時に使用できます</p>}
        </div>
        <div className="rounded-lg border border-zinc-200/60 bg-zinc-50 p-4" data-testid="ai-test-result">
          {!res && <p className="py-8 text-center text-xs text-zinc-400">結果がここに表示されます</p>}
          {res && (res.ok ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[['生成時間', `${(res.latency_ms / 1000).toFixed(1)}s`], ['速度', `${res.tokens_per_sec} tok/s`],
                  ['出力トークン(推定)', res.tokens_out], ['コンテキスト長', res.context_length ? res.context_length.toLocaleString() : '—']].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-zinc-200/60 bg-white px-3 py-2">
                    <p className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400">{k}</p>
                    <p className="text-sm font-semibold tabular-nums text-zinc-800">{v}</p>
                  </div>
                ))}
              </div>
              <p className="whitespace-pre-wrap border-t border-zinc-200 pt-3 text-[13px] leading-relaxed text-zinc-800">{res.reply}</p>
              {(res.citations || []).length > 0 && (
                <div className="space-y-0.5 border-t border-zinc-200 pt-2" data-testid="ai-test-citations">
                  {res.citations.map((c) => (
                    <a key={c.n} href={c.source_type === 'doc' ? `/documents?open=${c.article_id}` : `/knowledge/p/${c.article_id}`}
                      target="_blank" rel="noreferrer"
                      className="group flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-indigo-700">
                      <span className={`shrink-0 rounded border px-1 py-px text-[9px] font-bold ${c.source_type === 'doc'
                        ? 'border-violet-100 bg-violet-50 text-violet-600'
                        : 'border-indigo-100 bg-indigo-50 text-indigo-600'}`}>
                        {c.source_type === 'doc' ? '書類' : 'ナレッジ'}
                      </span>
                      <span className="truncate group-hover:underline">[{c.n}] {c.title}（関連度 {c.score}）</span>
                    </a>))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-[12px] font-semibold text-red-600">✗ {res.error}</p>
          ))}
        </div>
      </div>
    </Card>
  );
}
